import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, BookOpen, Headphones, PenTool, Mic, BarChart2, FileText, Settings, Users, LogOut, Menu, X, ChevronDown, Shield, GraduationCap, Gift, Newspaper, ClipboardList, DollarSign, XCircle, BarChart3, HelpCircle, Calendar, Sparkles, Home, Percent, Mail, Bot, LifeBuoy, Trophy, MessageCircle, RotateCcw, CreditCard, TrendingUp, Globe, MessageSquareText, Bell, PhoneCall, Route, Calculator, Crown, Hourglass } from 'lucide-react';
import { TIMEZONES, useAdminTz, tzAbbr } from '../lib/adminTime';
import NotificationBell from './NotificationBell';
import CreditsPill from './CreditsPill';
import WalletPill from './WalletPill';
import ProWelcomeModal from './ProWelcomeModal';
import TrialPill from './trial/TrialPill';
import { MyClassTab } from './nav/MyClassTab';
import { ProTab, SidebarProButton, useShowProCta } from './nav/ProCta';
import { useTrial } from '../contexts/TrialContext';
import { useDashboardWidgets } from '../lib/dashboardWidgets';

// Practice routes hidden from navigation for all users (not deleted) — pages
// remain reachable via direct URL. Remove a path here to show it again.
const HIDDEN_NAV_PATHS = ['/practice/listening', '/practice/reading', '/practice/writing', '/practice/speaking'];

// Single source of truth for the logged-in user navigation (sidebar +
// mobile menu). Blog intentionally removed from the dashboard menus.
const USER_NAV_LINKS = [
  { to: '/start-here', label: 'Start Here', icon: <Sparkles size={18} /> },
  { to: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} /> },
  { to: '/study-plan', label: 'My Study Plan', icon: <ClipboardList size={18} /> },
  { to: '/exams', label: 'Mock Exams', icon: <FileText size={18} /> },
  { to: '/practice/listening', label: 'Listening', icon: <Headphones size={18} /> },
  { to: '/practice/reading', label: 'Reading', icon: <BookOpen size={18} /> },
  { to: '/practice/writing', label: 'Writing', icon: <PenTool size={18} /> },
  { to: '/practice/speaking', label: 'Speaking', icon: <Mic size={18} /> },
  { to: '/courses', label: 'Courses', icon: <GraduationCap size={18} /> },
  { to: '/group-classes', label: 'Group Classes', icon: <Calendar size={18} /> },
  { to: '/performance', label: 'Performance', icon: <BarChart2 size={18} /> },
  { to: '/feedback-credits', label: 'Teacher Feedback', icon: <MessageSquareText size={18} /> },
  { to: '/crs-calculator', label: 'CRS Calculator', icon: <Calculator size={18} /> },
  { to: '/notifications', label: 'Notifications', icon: <Bell size={18} /> },
  { to: '/my-tickets', label: 'My Tickets', icon: <LifeBuoy size={18} /> },
  { to: '/referral', label: 'Referral', icon: <Gift size={18} /> },
];

const VISIBLE_USER_NAV_LINKS = USER_NAV_LINKS.filter(l => !HIDDEN_NAV_PATHS.includes(l.to));

// Role-based staff panel entry points + labels (RBAC).
export const STAFF_PANEL_HOME = { admin: '/admin', teacher: '/admin/users', contributor: '/admin/exams', sales: '/admin/sales' };
const STAFF_PANEL_LABEL = { admin: 'Admin', teacher: 'Teacher Panel', contributor: 'Contributor Panel', sales: 'Sales Panel' };

export function Navbar() {
  const { user, logout } = useAuth();
  const { entryLabel, trialMode } = useTrial();
  const widgets = useDashboardWidgets(!!user);
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <nav className="sticky top-0 z-50 glass-surface border-b border-slate-200/60" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2.5 group shrink-0" data-testid="nav-logo">
            <img src="/logo-navbar.png" alt="CELPIPMax" className="w-9 h-9" />
            <span className={`font-[Manrope] font-extrabold text-lg text-slate-900 tracking-tight ${user ? 'hidden sm:inline' : ''}`}>CELPIPMax</span>
          </Link>

          {user ? (
            <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
              <div className="hidden md:flex items-center gap-1">
                <NavLink to="/dashboard" icon={<LayoutDashboard size={15} />}>Dashboard</NavLink>
                <NavLink to="/exams" icon={<FileText size={15} />}>Exams</NavLink>
                <NavLink to="/performance" icon={<BarChart2 size={15} />}>Performance</NavLink>
                {STAFF_PANEL_HOME[user.role] && <NavLink to={STAFF_PANEL_HOME[user.role]} icon={<Shield size={15} />}>{STAFF_PANEL_LABEL[user.role]}</NavLink>}
              </div>
              <TrialPill />
              {widgets.header_wallet_pill && <WalletPill />}
              <CreditsPill />
              <NotificationBell />
              <div className="relative">
                <button onClick={() => setDropdownOpen(!dropdownOpen)} className="flex items-center gap-2 px-1 sm:px-3 py-1.5 rounded-full hover:bg-slate-100 transition-colors" data-testid="user-menu-btn">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm shadow-sm">
                    {user.name?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
                  </div>
                  <ChevronDown size={14} className="text-slate-400 hidden sm:block" />
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 z-50 animate-scale-in" data-testid="user-dropdown">
                    <div className="px-4 py-2.5 border-b border-slate-100">
                      <p className="text-sm font-semibold text-slate-900 truncate">{user.name || user.email}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{user.subscription_status === 'free' ? `${entryLabel} Plan` : 'Pro Member'}</p>
                    </div>
                    <Link to="/settings" className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 transition-colors" onClick={() => setDropdownOpen(false)}>
                      <Settings size={14} />Settings
                    </Link>
                    <button onClick={() => { logout(); navigate('/'); setDropdownOpen(false); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors" data-testid="logout-btn">
                      <LogOut size={14} />Logout
                    </button>
                  </div>
                )}
              </div>
              <button className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link to="/winning-stories" className="hidden md:block text-sm text-slate-600 hover:text-slate-900 font-medium transition-colors" data-testid="nav-winning-stories">Winning Stories</Link>
              <Link to="/crs-calculator" className="hidden md:block text-sm text-slate-600 hover:text-slate-900 font-medium transition-colors" data-testid="nav-crs-calculator">CRS Calculator</Link>
              <Link to="/pricing" className="hidden sm:block text-sm text-slate-600 hover:text-slate-900 font-medium transition-colors" data-testid="nav-pricing">Pricing</Link>
              <Link to="/blog" className="hidden sm:block text-sm text-slate-600 hover:text-slate-900 font-medium transition-colors" data-testid="nav-blog">Blog</Link>
              <Link to="/login" className="text-sm font-medium text-slate-700 hover:text-primary transition-colors px-3 py-2" data-testid="nav-login">Log in</Link>
              <Link to="/signup" className="btn-primary !py-2.5 !px-6 !text-sm" data-testid="nav-signup">{trialMode ? 'Start Trial' : 'Start Free'}</Link>
            </div>
          )}
        </div>
      </div>
      {mobileOpen && user && (
        <div className="md:hidden border-t border-slate-100 bg-white px-3 py-3 animate-slide-down max-h-[75vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="grid grid-cols-2 gap-1.5">
            {VISIBLE_USER_NAV_LINKS.map(link => (
              <MobileNavLink key={link.to} to={link.to} icon={link.icon} onClick={() => setMobileOpen(false)}>{link.label}</MobileNavLink>
            ))}
            <MobileNavLink to="/whats-included" icon={<Crown size={18} />} onClick={() => setMobileOpen(false)}>What's in Pro</MobileNavLink>
            <MobileNavLink to="/settings" icon={<Settings size={18} />} onClick={() => setMobileOpen(false)}>Settings</MobileNavLink>
            <MobileNavLink to="/contact" icon={<LifeBuoy size={18} />} onClick={() => setMobileOpen(false)}>Contact Us</MobileNavLink>
            {STAFF_PANEL_HOME[user.role] && <MobileNavLink to={STAFF_PANEL_HOME[user.role]} icon={<Shield size={18} />} onClick={() => setMobileOpen(false)}>{STAFF_PANEL_LABEL[user.role]}</MobileNavLink>}
          </div>
        </div>
      )}
    </nav>
  );
}

function NavLink({ to, children, icon }) {
  const location = useLocation();
  const active = location.pathname === to || location.pathname.startsWith(to + '/');
  return (
    <Link to={to} className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-full transition-all ${active ? 'bg-blue-50 text-[#2563EB] font-semibold' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50 font-medium'}`}>
      {icon}{children}
    </Link>
  );
}

function MobileNavLink({ to, children, onClick, icon }) {
  const location = useLocation();
  const active = location.pathname === to || location.pathname.startsWith(to + '/');
  return (
    <Link to={to} onClick={onClick} className={`flex items-center gap-2.5 px-3.5 py-3 rounded-xl text-sm font-medium transition-colors ${active ? 'bg-blue-50 text-[#2563EB]' : 'text-slate-600 hover:bg-slate-50'}`}>
      {icon && <span className={active ? 'text-[#2563EB]' : 'text-slate-400'}>{icon}</span>}
      {children}
    </Link>
  );
}

/* Bottom tab bar for mobile — Home · Course · Pro (free students) / Study Plan (Pro members) · Tests · Class (next-class mini timer). Solid background: no blur on phones. */
const TAB_BASE = 'flex flex-col items-center gap-0.5 py-2 text-[10.5px] font-semibold transition-colors';
const TAB_ACTIVE = 'text-[#2563EB]';
const TAB_IDLE = 'text-slate-400 hover:text-slate-600';
export function MobileBottomNav() {
  const location = useLocation();
  const showPro = useShowProCta();
  const tabs = [
    { to: '/dashboard', id: 'home', label: 'Home', icon: <Home size={20} /> },
    { to: '/courses', id: 'courses', label: 'Course', icon: <GraduationCap size={20} /> },
    showPro ? { key: 'pro' } : { to: '/study-plan', id: 'study-plan', label: 'Study Plan', icon: <ClipboardList size={20} /> },
    { to: '/exams', id: 'exams', label: 'Tests', icon: <FileText size={20} /> },
    { key: 'my-class' },
  ];
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_16px_rgba(15,23,42,0.05)]" data-testid="mobile-bottom-nav" data-plan={showPro ? 'free' : 'pro'}>
      <div className="grid grid-cols-5">
        {tabs.map(t => {
          if (t.key === 'my-class') return <MyClassTab key="my-class" className={TAB_BASE} activeClass={TAB_ACTIVE} idleClass={TAB_IDLE} />;
          if (t.key === 'pro') return <ProTab key="pro" className={TAB_BASE} />;
          const active = location.pathname === t.to || location.pathname.startsWith(t.to + '/');
          return (
            <Link key={t.to} to={t.to} data-testid={`bottom-nav-${t.id}`}
              className={`${TAB_BASE} ${active ? TAB_ACTIVE : TAB_IDLE}`}>
              <span className={`px-3 py-0.5 rounded-full transition-colors ${active ? 'bg-blue-50' : ''}`}>{t.icon}</span>
              {t.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export function DashboardLayout({ children, title }) {
  const { user } = useAuth();
  const location = useLocation();
  const showPro = useShowProCta();

  const sideLinks = [...VISIBLE_USER_NAV_LINKS];
  if (STAFF_PANEL_HOME[user?.role]) {
    sideLinks.push({ to: STAFF_PANEL_HOME[user.role], label: STAFF_PANEL_LABEL[user.role], icon: <Shield size={18} /> });
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="flex">
        <aside className="hidden lg:flex flex-col w-[240px] border-r border-slate-200/60 bg-white min-h-[calc(100vh-64px)] p-4 sticky top-16" data-testid="sidebar">
          {showPro && <SidebarProButton />}
          <nav className="space-y-0.5 flex-1">
            {sideLinks.map(link => {
              const active = location.pathname === link.to || location.pathname.startsWith(link.to + '/');
              return (
                <Link key={link.to} to={link.to} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${active ? 'bg-blue-50 text-[#2563EB] font-semibold shadow-sm shadow-blue-500/5' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800 font-medium'}`}>
                  <span className={active ? 'text-[#2563EB]' : 'text-slate-400'}>{link.icon}</span>
                  <span>{link.label}</span>
                </Link>
              );
            })}
          </nav>
          <div className="pt-3 border-t border-slate-100 mt-3">
            <Link to="/whats-included" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all font-medium ${location.pathname === '/whats-included' ? 'bg-amber-50 text-amber-800 font-semibold' : 'text-slate-400 hover:bg-amber-50/60 hover:text-amber-800'}`} data-testid="sidebar-whats-included-link">
              <Crown size={18} className="text-amber-500" /><span>What's in Pro</span>
            </Link>
            <Link to="/settings" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-400 hover:bg-slate-50 hover:text-slate-700 transition-all font-medium">
              <Settings size={18} /><span>Settings</span>
            </Link>
            <Link to="/contact" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-400 hover:bg-slate-50 hover:text-slate-700 transition-all font-medium" data-testid="sidebar-contact-link">
              <LifeBuoy size={18} /><span>Contact Us</span>
            </Link>
          </div>
        </aside>
        <main className="flex-1 min-w-0 p-3 sm:p-4 md:p-6 lg:p-8 pb-24 lg:pb-8 max-w-6xl">
          <div>
            {title && <h1 className="font-[Manrope] text-xl sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6 tracking-tight" data-testid="page-title">{title}</h1>}
            {children}
          </div>
        </main>
      </div>
      <MobileBottomNav />
      <ProWelcomeModal />
    </div>
  );
}

export function AdminTimezonePicker({ compact = false }) {
  const [tz, setTz] = useAdminTz();
  return (
    <label className={`flex items-center gap-2 ${compact ? '' : 'px-3'}`} title="Timezone used for every date and chart in the admin panel">
      <Globe size={14} className="text-slate-400 shrink-0" />
      <select value={tz} onChange={(e) => setTz(e.target.value)} data-testid={compact ? 'admin-tz-select-mobile' : 'admin-tz-select'}
        className="w-full min-w-0 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-200">
        {TIMEZONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
      <span className="text-[10px] font-semibold text-slate-400 shrink-0" data-testid={compact ? 'admin-tz-abbr-mobile' : 'admin-tz-abbr'}>{tzAbbr(tz)}</span>
    </label>
  );
}

export function AdminLayout({ children, title }) {
  const location = useLocation();
  const { user } = useAuth();
  const role = user?.role;
  const allAdminLinks = [
    { to: '/admin', label: 'Dashboard', icon: <LayoutDashboard size={18} />, roles: ['admin'] },
    { to: '/admin/revenue', label: 'Revenue', icon: <BarChart3 size={18} />, roles: ['admin'] },
    { to: '/admin/marketing', label: 'Marketing & Sales', icon: <TrendingUp size={18} />, roles: ['admin'] },
    { to: '/admin/sales', label: 'Sales CRM', icon: <PhoneCall size={18} />, roles: ['admin', 'sales'] },
    { to: '/admin/growth', label: 'Student e-mails · Growth Journeys', icon: <Route size={18} />, roles: ['admin'] },
    { to: '/admin/study-plans', label: 'Study Plans', icon: <ClipboardList size={18} />, roles: ['admin', 'teacher'] },
    { to: '/admin/teacher', label: 'Teacher Feedback', icon: <MessageSquareText size={18} />, roles: ['admin', 'teacher'] },
    { to: '/admin/questions', label: 'Questions', icon: <FileText size={18} />, roles: ['admin', 'contributor'] },
    { to: '/admin/exams', label: 'Exams', icon: <BookOpen size={18} />, roles: ['admin', 'contributor'] },
    { to: '/admin/users', label: 'Users', icon: <Users size={18} />, roles: ['admin', 'teacher', 'sales'] },
    { to: '/admin/referrals', label: 'Referrals', icon: <Gift size={18} />, roles: ['admin'] },
    { to: '/admin/blog', label: 'Blog', icon: <Newspaper size={18} />, roles: ['admin'] },
    { to: '/admin/courses', label: 'Courses', icon: <GraduationCap size={18} />, roles: ['admin', 'contributor'] },
    { to: '/admin/events', label: 'Group Classes', icon: <Calendar size={18} />, roles: ['admin', 'teacher'] },
    { to: '/admin/start-here', label: 'Start Here Page', icon: <Sparkles size={18} />, roles: ['admin'] },
    { to: '/admin/student-dashboard', label: 'Student Dashboard', icon: <LayoutDashboard size={18} />, roles: ['admin'] },
    { to: '/admin/landing', label: 'Landing Page', icon: <Home size={18} />, roles: ['admin'] },
    { to: '/admin/contact-page', label: 'Contact Page', icon: <Mail size={18} />, roles: ['admin'] },
    { to: '/admin/legal', label: 'Legal Pages', icon: <FileText size={18} />, roles: ['admin'] },
    { to: '/admin/onboarding', label: 'Onboarding Form', icon: <ClipboardList size={18} />, roles: ['admin'] },
    { to: '/admin/onboarding/responses', label: 'Responses', icon: <ClipboardList size={18} />, roles: ['admin', 'teacher'] },
    { to: '/admin/pricing', label: 'Pricing Page', icon: <DollarSign size={18} />, roles: ['admin'] },
    { to: '/admin/promotions', label: 'Popups / Promotions', icon: <Percent size={18} />, roles: ['admin'] },
    { to: '/admin/checkout', label: 'Checkout Page', icon: <CreditCard size={18} />, roles: ['admin'] },
    { to: '/admin/emails', label: 'Admin alerts (e-mails to you)', icon: <Mail size={18} />, roles: ['admin'] },
    { to: '/admin/notifications', label: 'Notifications', icon: <Bell size={18} />, roles: ['admin'] },
    { to: '/admin/support', label: 'Support', icon: <LifeBuoy size={18} />, roles: ['admin'] },
    { to: '/admin/whatsapp', label: 'WhatsApp', icon: <MessageCircle size={18} />, roles: ['admin'] },
    { to: '/admin/refunds', label: 'Refunds', icon: <RotateCcw size={18} />, roles: ['admin'] },
    { to: '/admin/trial', label: 'Free vs Trial', icon: <Hourglass size={18} />, roles: ['admin'] },
    { to: '/admin/ai-prompts', label: 'AI Prompts', icon: <Bot size={18} />, roles: ['admin'] },
    { to: '/admin/ai-health', label: 'AI Health (speaking)', icon: <Mic size={18} />, roles: ['admin'] },
    { to: '/admin/crs', label: 'CRS Calculator', icon: <BarChart2 size={18} />, roles: ['admin'] },
    { to: '/admin/cancellations', label: 'Cancellations', icon: <XCircle size={18} />, roles: ['admin'] },
    { to: '/admin/help', label: 'Help', icon: <HelpCircle size={18} />, roles: ['admin'] },
  ];
  // Owner request (iter 132): every panel link in alphanumeric order.
  const adminLinks = allAdminLinks.filter(l => l.roles.includes(role)).sort((a, b) => a.label.localeCompare(b.label, 'en', { numeric: true, sensitivity: 'base' }));
  const panelLabel = role === 'teacher' ? 'Teacher Panel' : role === 'contributor' ? 'Contributor Panel' : role === 'sales' ? 'Sales Panel' : 'Admin Panel';

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="flex">
        <aside className="hidden lg:flex flex-col w-[240px] border-r border-slate-200/60 bg-white min-h-[calc(100vh-64px)] p-4 sticky top-16">
          <div className="mb-4 px-3 py-2 bg-amber-50 rounded-xl border border-amber-200/60">
            <p className="text-xs font-semibold text-amber-700" data-testid="admin-panel-badge">{panelLabel}</p>
          </div>
          <nav className="space-y-0.5 flex-1">
            {adminLinks.map(link => {
              const active = location.pathname === link.to;
              return (
                <Link key={link.to} to={link.to} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${active ? 'bg-blue-50 text-[#2563EB] font-semibold' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800 font-medium'}`}>
                  <span className={active ? 'text-[#2563EB]' : 'text-slate-400'}>{link.icon}</span>
                  <span>{link.label}</span>
                </Link>
              );
            })}
          </nav>
          <div className="pt-3 border-t border-slate-100 mt-3 space-y-2">
            <div>
              <p className="px-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Dates shown in</p>
              <AdminTimezonePicker />
            </div>
            <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-400 hover:bg-slate-50 hover:text-slate-700 transition-all font-medium">
              <LayoutDashboard size={16} />Back to Dashboard
            </Link>
          </div>
        </aside>
        <main className="flex-1 min-w-0 p-4 md:p-6 lg:p-8">
          <div className="lg:hidden mb-4 -mx-1 overflow-x-auto" data-testid="admin-mobile-nav">
            <div className="flex gap-1.5 w-max px-1 pb-1">
              {adminLinks.map(link => {
                const active = location.pathname === link.to;
                return (
                  <Link key={link.to} to={link.to} className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors ${active ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
                    <span className={active ? 'text-white' : 'text-slate-400'}>{React.cloneElement(link.icon, { size: 14 })}</span>
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>
          <div className="lg:hidden mb-4 max-w-xs"><AdminTimezonePicker compact /></div>
          <div>
            {title && <h1 className="font-[Manrope] text-2xl font-bold text-slate-900 mb-6 tracking-tight">{title}</h1>}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
